/**
 * The volumes an Ember humanoid is drawn from, hung on a solved skeleton.
 *
 * Clothing is geometry, not a painted layer: a garment re-uses the body's own volumes, slightly
 * inflated, in its own material. The raster keeps whichever surface is nearest the camera, so a
 * sleeve covers the arm, the arm passes in front of the shirt, and a cape falls behind the back,
 * frame by frame and facing by facing, with no layer order to get wrong. A new pose costs nothing
 * per garment.
 *
 * This file is the BODY -- flesh, bones, hair, a beast's head -- and the vocabulary everything else
 * is made of: the body's own volumes, the primitive shapes, light and paint. What a body wears and
 * holds is data: models in content (sprites/ember.json, model.ts), run here in dressed().
 */
import { BONE, BONES, DYES, Material, SKIN, dye, hairDye, metalFrom, rampFrom } from "./palette"
import { EmberSlot, OverBody, emberDesign, emberDesigns, onEmberModelsChanged, runEmberModel, setModelBuiltins } from "./model"
import type { Facing } from "./raster"
import { DEFAULT_RASTER, FACING_YAW } from "./raster"
import { LimbJoints, Proportions, SIDES, Side, Skeleton, farSide, legLength, sideSign } from "./rig"
import { IDENTITY, Mat3, Vec3, add, apply, cross, deg, distance, dot, length as vecLength, lerp, mul, normalize, rotX, rotY, rotZ, scale, sub, transpose } from "./vec"
import "./content"

/** Which body part a pixel belongs to. Edges between parts are drawn as creases. */
/** `skirt`: one garment round both legs, shaded as torso cloth but outlined against the legs. */
export type PartGroup = "head" | "hair" | "torso" | "bust" | "shoulder" | "armL" | "armR" | "legL" | "legR" | "held" | "skirt" | "tail"

interface PrimitiveBase {
    material: Material
    group: PartGroup
    /**
     * Which segment of its limb this is. Where two segments of one limb meet -- elbow, wrist, knee,
     * ankle -- a line is drawn, as LPC draws every joint; within a segment, none.
     */
    part?: string
    /** Optional colour painted onto the surface, from the hit point in the primitive's own frame. */
    decal?: (local: Vec3) => number | undefined
}

export interface Capsule extends PrimitiveBase {
    kind: "capsule"
    a: Vec3
    b: Vec3
    r: number
}

export interface Ellipsoid extends PrimitiveBase {
    kind: "ellipsoid"
    center: Vec3
    /** Columns are the ellipsoid's local x, y, z axes. */
    basis: Mat3
    radii: Vec3
    /**
     * Squareness: 2 is an ellipsoid; higher flattens toward a box with rounded edges
     * (|x/a|^ex + |y/b|^ey + |z/c|^ez = 1), one number for all axes or one per axis. LPC heads and
     * chests are flat-sided from the front but round in profile: square across, round front to back.
     */
    exponent?: number | Vec3
    /** Keep only the parts of the shell for which this holds (local, unscaled coordinates). */
    keep?: (local: Vec3) => boolean
}

export type Primitive = Capsule | Ellipsoid

export type Sleeves = "none" | "short" | "long"
/** Ember's own hair styles; a look may also name any of the game's (LPC's) styles (HAIR_DESIGNS). */
export type HairStyle = "bald" | "crop" | "short" | "long" | "ponytail" | string

/** What an Ember humanoid wears and looks like. Data, so a new character is a new record. */
export interface EmberLook {
    skin: keyof typeof SKIN | string
    /** A skeleton: bones where the flesh would be (bonesOf), a skull without eyes or hair. */
    bones?: boolean
    /** Its bone's colour, a BONES name (`green`): the game's skeleton body colour. Ivory unless named. */
    boneColor?: string
    /** Torn flesh: open wounds painted over the body (woundsOf), as LPC's zombie has. */
    wounds?: boolean
    /**
     * A beast on the man's rig: `wolf`, LPC's wolfman -- a wolf's head (wolfHeadOf), a ruff of fur at
     * the neck and a tail, fur for skin (palette `wolf-<colour>`), and no hair.
     */
    beast?: "wolf"
    /** An EYE_STYLES name (face.ts); Ember's own by default. */
    eyes?: string
    hair?: { style: HairStyle; color: string }
    /**
     * A shirt: its sleeves, `tunic` over the hips (with `hem: "thigh"` a skirt to mid-thigh), and
     * what shirtOf adds -- a neckline, puffed shoulders, a pattern.
     */
    shirt?: {
        dye: string
        /** A shirt model other than the game's `shirt` (model.ts), which the rest are parameters of. */
        model?: string
        sleeves: Sleeves
        tunic?: boolean
        hem?: "thigh"
        /** `scoop` a round neck, `low` a deep one with the shoulders bare but for straps, `laced` a laced V. */
        neck?: "scoop" | "low" | "laced"
        puffed?: boolean
        pattern?: "stripes" | "lacing"
    }
    /**
     * Leg wear, `trousers` unless its `cut` says otherwise: `shorts` to the knee, `pantaloons` loose
     * and gathered below the calf (pantaloonsOf), a `skirt` round both legs (skirtOf), a legionary's
     * strapped `legion` skirt (legionOf), steel `greaves` (greavesOf; `dye` names a metal).
     */
    pants?: { dye: string; cut?: PantsCut; model?: string }
    /** Footwear, `shoes` unless its `cut` says otherwise (footwearOf); sabatons name a metal. */
    shoes?: { dye: string; cut?: ShoeCut; model?: string }
    belt?: { dye: string; model?: string }
    /** Body armour over the shirt (armorOf): its kind and colour. Equipment, like `held`. */
    armor?: { kind: ArmorKind; color: string; model?: string }
    /**
     * The game's bauldron (bauldronOf): a leather pauldron on the right shoulder, strapped across the
     * chest to the left side, over whatever is worn there. Equipment, like `held`.
     */
    bauldron?: { color: string; model?: string }
    /**
     * A shield (shieldOf): the game's shield item type and colour, in the left fist, or `slung` on
     * the back while that hand has a bow. Equipment, like `held`.
     */
    shield?: { type: string; color: string; slung?: boolean }
    /** What the right hand holds: an item type with a `held` design (`axe`). Equipment, not a fixed part of the look. */
    held?: string
    /** The held item's colour variant, as the item names it (`metal`, `brown`). */
    heldColor?: string
}

const capsule = (a: Vec3, b: Vec3, r: number, material: Material, group: PartGroup): Capsule => ({
    kind: "capsule",
    a,
    b,
    r,
    material,
    group,
})

const ellipsoid = (
    center: Vec3,
    basis: Mat3,
    radii: Vec3,
    material: Material,
    group: PartGroup,
    extra: Partial<Ellipsoid> = {},
): Ellipsoid => ({ kind: "ellipsoid", center, basis, radii, material, group, ...extra })

const inflate = (r: Vec3, by: number): Vec3 => [r[0] + by, r[1] + by, r[2] + by]

/**
 * A limb segment shaped like muscle rather than pipe: an ellipsoid along the bone, fullest in the
 * middle and narrowing toward both joints, where a smaller ball (`joint`) keeps the bend filled.
 * A tube of constant radius reads as a mannequin; this reads as an upper arm, a calf.
 */
const segment = (a: Vec3, b: Vec3, r: number, m: Material, group: PartGroup, exponent?: number): Ellipsoid => {
    const along = sub(b, a)
    const half = Math.sqrt(dot(along, along)) / 2
    const y = normalize(along)
    const x = normalize(Math.abs(y[2]) < 0.9 ? cross(y, [0, 0, 1]) : cross(y, [1, 0, 0]))
    const z = cross(x, y)
    const basis: Mat3 = [x[0], y[0], z[0], x[1], y[1], z[1], x[2], y[2], z[2]]
    return ellipsoid(scale(add(a, b), 0.5), basis, [r, half + r * 0.45, r], m, group, exponent ? { exponent } : {})
}
/**
 * A foot: an ellipsoid from heel to toe, as tall as `r` and `width` across, so from the front it is
 * the broad wedge LPC draws below the ankle rather than a peg the width of the shin.
 */
const foot = (heel: Vec3, toe: Vec3, r: number, width: number, m: Material, group: PartGroup): Ellipsoid => {
    const along = sub(toe, heel)
    const half = Math.sqrt(dot(along, along)) / 2
    const z = normalize(along)
    const x = normalize(cross([0, 1, 0], z))
    const y = cross(z, x)
    const basis: Mat3 = [x[0], y[0], z[0], x[1], y[1], z[1], x[2], y[2], z[2]]
    // Squared across and underneath: a flat sole, not a rocker.
    return ellipsoid(scale(add(heel, toe), 0.5), basis, [width, r, half + r], m, group, { exponent: [3, 3, 2.2] })
}
const joint = (at: Vec3, r: number, m: Material, group: PartGroup): Ellipsoid =>
    ellipsoid(at, [1, 0, 0, 0, 1, 0, 0, 0, 1], [r, r, r], m, group)

/** Name the limb segment a primitive is (see PrimitiveBase.part). */
const as = <T extends Primitive>(prim: T, part: string): T => ({ ...prim, part })

/** Which arm/leg group a side's limbs belong to. */
const armOf = (side: "L" | "R"): PartGroup => (side === "L" ? "armL" : "armR")
const legOf = (side: "L" | "R"): PartGroup => (side === "L" ? "legL" : "legR")

/**
 * A frame square to a leg bone from `a` to `b`: `along` it, `ahead` the way its foot points, `out`
 * away from the other leg; `basis` is (x, along, ahead) for ellipsoids laid along the bone.
 */
const legFrame = (sk: Skeleton, side: Side, a: Vec3, b: Vec3) => {
    const along = normalize(sub(b, a))
    const square = (v: Vec3) => sub(v, scale(along, dot(v, along)))
    const toe = square(sub(sk[side].toe, sk[side].heel))
    const ahead = normalize(dot(toe, toe) > 1e-4 ? toe : square(apply(sk.pelvisBasis, [0, 0, 1])))
    const x = cross(along, ahead)
    const basis: Mat3 = [x[0], along[0], ahead[0], x[1], along[1], ahead[1], x[2], along[2], ahead[2]]
    const out = scale(x, dot(x, apply(sk.pelvisBasis, [sideSign(side), 0, 0])) < 0 ? -1 : 1)
    return { along, ahead, out, basis, length: distance(a, b) }
}

/**
 * A thigh, `by` off the skin (garments pass how far they stand off it): one muscle-shaped segment,
 * and on an athlete's legs (`quad`) the quadriceps swelling it in front and to the outside, fullest
 * a third of the way down and drawn in to the knee -- a thigh that reads strong, not just thick.
 */
export const TUNE_LEG = { quadDown: 0.38, quadAhead: 0.3, quadOut: 0.15, quadWidth: 1, quadLength: 0.4, quadDepth: 0.9, calfBack: 0.45, calfLength: 0.28, calfWidth: 1, calfDepth: 0.85, heads: [[0.3, 0.3, 0.9], [0.36, -0.35, 1]] }
const thighOf = (sk: Skeleton, side: Side, p: Proportions, by: number, m: Material, g: PartGroup): Ellipsoid[] => {
    const j = sk[side]
    const prims = [segment(j.hip, j.knee, p.thighRadius + by, m, g)]
    if (p.quad) {
        const f = legFrame(sk, side, j.hip, j.knee)
        const L = TUNE_LEG
        const at = add(lerp(j.hip, j.knee, L.quadDown), add(scale(f.ahead, p.thighRadius * L.quadAhead), scale(f.out, p.thighRadius * L.quadOut)))
        prims.push(ellipsoid(at, f.basis, [p.quad * L.quadWidth + by, f.length * L.quadLength + by, p.quad * L.quadDepth + by], m, g))
    }
    return prims
}

/**
 * A shin from the knee to `to` of the way to the ankle, `by` off the skin: one segment, and on an
 * athlete's legs (`calf`) the two heads of the calf behind its upper third -- the inner one fuller
 * and lower -- falling away to a slim ankle.
 */
const shinOf = (sk: Skeleton, side: Side, p: Proportions, by: number, m: Material, g: PartGroup, to = 1): Ellipsoid[] => {
    const j = sk[side]
    const prims = [segment(j.knee, to === 1 ? j.ankle : lerp(j.knee, j.ankle, to), p.shinRadius + by, m, g)]
    if (p.calf) {
        const f = legFrame(sk, side, j.knee, j.ankle)
        const L = TUNE_LEG
        const back = scale(f.ahead, -p.shinRadius * L.calfBack)
        for (const [down, outward, size] of L.heads) {
            const at = add(lerp(j.knee, j.ankle, down), add(back, scale(f.out, outward * p.shinRadius)))
            prims.push(ellipsoid(at, f.basis, [p.calf * size * L.calfWidth + by, f.length * L.calfLength + by, p.calf * size * L.calfDepth + by], m, g))
        }
    }
    return prims
}

/**
 * The skull: a rounded box, flat at the sides and across the face, widest at the cheeks -- the
 * shape the LPC head is drawn as -- with ears standing a pixel proud at eye level. The eyes are
 * not geometry: they are stamped on as LPC pixels (face.ts).
 */
const head = (sk: Skeleton, p: Proportions, skin: Material): Primitive[] => {
    const [a, b, c] = p.head
    const skull = add(sk.head, apply(sk.headBasis, [0, p.craniumLift, 0]))
    const prims: Primitive[] = [ellipsoid(skull, sk.headBasis, p.head, skin, "head", { exponent: p.headRoundness })]
    // The jaw: the lower face, forward and below the skull's base, so the chin is the lowest point
    // of the head and the line under the jaw slants back up to the neck. Tall and flat-fronted, so in
    // profile the face runs straight down from the brow to the chin, as LPC draws it, and only then
    // turns back under the jaw.
    const jaw = add(sk.head, apply(sk.headBasis, [0, -b * 0.5, c * 0.38]))
    // As wide as the cheeks, so from the front the face keeps the skull's outline to the chin.
    prims.push(ellipsoid(jaw, sk.headBasis, [a * 0.9, b * 0.62, c * 0.6], skin, "head", { exponent: [2.6, 3, 2.0] }))
    // Ears sized and set for the LPC head (10.05 x 8.7 x 9.15), scaled to this one's.
    const [kx, ky, kz] = [a / 10.05, b / 8.7, c / 9.15]
    for (const s of [1, -1]) {
        const ear = add(sk.head, apply(sk.headBasis, [s * (a - 0.5 * kx), p.earHeight, -0.8 * kz]))
        prims.push(ellipsoid(ear, sk.headBasis, [1.6 * kx, 2.3 * ky, 1.5 * kz], skin, "head"))
    }
    return prims
}

const WOLF_NOSE = rampFrom("#2a1f22", "#0c0809")
const WOLF_EAR = rampFrom("#c98a86")

/**
 * A wolf's head on the man's neck, as LPC's wolfman wears one: the skull, a long muzzle reaching
 * well past the face with a dark nose on its tip and a lower jaw under it, the fur of the jowls
 * filling out the cheeks, and tall pointed ears standing up off the crown, pink inside. Sized off
 * the man's own head (p.head), so the eyes (face.ts, the `wolf` style) still sit where his would.
 */
const wolfHeadOf = (sk: Skeleton, p: Proportions, fur: Material, facing: Facing): Primitive[] => {
    const [a, b, c] = p.head
    const at = (v: Vec3) => add(sk.head, apply(sk.headBasis, v))
    // The muzzle a shade darker than the coat, as LPC paints it: front-on, it is what makes the face a wolf's.
    const muzzle: Material = { ...fur, light: fur.base, base: fur.shade, shade: fur.deep, deep: fur.line }
    // The muzzle's dark runs on up the bridge between the eyes to the brow.
    const lit = lighting(facing)
    const toHead = transpose(sk.headBasis)
    const skull = painted([ellipsoid(at([0, p.craniumLift * 0.5, -c * 0.05]), sk.headBasis, [a * 0.95, b * 0.88, c * 0.95], fur, "head", { exponent: p.headRoundness })],
        (w, n) => {
            const q = apply(toHead, sub(w, sk.head))
            const width = a * (0.22 - 0.1 * Math.max(0, q[1] / b))
            return q[2] > c * 0.3 && Math.abs(q[0]) < width && q[1] > -b * 0.3 && q[1] < b * 0.45 ? tone(muzzle, lit(n)) : undefined
        })
    const prims: Primitive[] = [
        ...skull,
        // The muzzle, square-ish in section, its tip well ahead of where a man's face would be.
        ellipsoid(at([0, -b * 0.32, c * 1.0]), sk.headBasis, [a * 0.42, b * 0.38, c * 0.8], muzzle, "head", { exponent: [2.2, 2.6, 2] }),
        ellipsoid(at([0, -b * 0.62, c * 0.72]), sk.headBasis, [a * 0.36, b * 0.22, c * 0.6], muzzle, "head"),
        ellipsoid(at([0, -b * 0.16, c * 1.8]), sk.headBasis, [Math.max(0.9, a * 0.22), Math.max(0.8, b * 0.18), 0.8], WOLF_NOSE, "head"),
    ]
    for (const s of [1, -1]) prims.push(ellipsoid(at([s * a * 0.7, -b * 0.4, -c * 0.1]), sk.headBasis, [a * 0.36, b * 0.48, c * 0.6], fur, "head"))
    // The ears: leaves standing up and a little out from the crown, their fronts pink.
    const ahead = apply(sk.headBasis, [0, 0, 1])
    const ears = [1, -1].map(s => segment(at([s * a * 0.5, b * 0.5, -c * 0.1]), at([s * a * 0.95, b * 1.6, -c * 0.3]), Math.max(1.3, a * 0.34), fur, "head"))
    prims.push(...painted(ears, (_w, n) => (dot(n, ahead) > 0.4 ? tone(WOLF_EAR, lit(n)) : undefined)))
    return prims
}

/**
 * The rest of a wolfman's wolf: a thick ruff of fur round the neck, setting the head down into the
 * shoulders as LPC draws it, and a bushy tail from the base of the spine, outlined as a part of its own.
 */
const wolfFurOf = (sk: Skeleton, p: Proportions, fur: Material): Primitive[] => {
    const leg = legLength(p)
    const ruff = ellipsoid(add(sk.neck, apply(sk.torsoBasis, [0, -p.neckRadius * 0.3, -p.neckRadius * 0.2])), sk.torsoBasis,
        [p.neckRadius * 2.0, p.neckRadius * 1.35, p.neckRadius * 1.75], fur, "torso")
    const root = add(sk.pelvis, apply(sk.pelvisBasis, [0, -p.pelvis[1] * 0.15, -p.pelvis[2] * 0.85]))
    const mid = add(root, apply(sk.pelvisBasis, [0, -leg * 0.2, -leg * 0.14]))
    const tip = add(root, apply(sk.pelvisBasis, [0, -leg * 0.46, -leg * 0.16]))
    const r = Math.max(1.3, p.thighRadius * 0.45)
    return [ruff, segment(root, mid, r, fur, "tail"), segment(mid, tip, r * 1.35, fur, "tail")]
}

/**
 * Chest, shoulders and neck. The shoulder girdle is the top of a wide dome, so the line from the
 * neck to each deltoid SLOPES, and each deltoid caps its arm round: an arm is hung under a
 * shoulder, not bolted to the corner of a box. `by` inflates every volume (garments).
 */
const upperBody = (sk: Skeleton, p: Proportions, m: Material, by = 0, facing: Facing = "front"): Primitive[] => {
    // The chest carried a little forward of the spine: upright, chest out, not a straight post.
    const chest = add(sk.chest, apply(sk.torsoBasis, [0, 0, p.posture]))
    const prims: Primitive[] = [
        ellipsoid(chest, sk.torsoBasis, inflate(p.torso, by), m, "torso", { exponent: p.torsoRoundness }),
    ]
    // A garment's girdle grows out and around but not UP: lowered by what it is inflated by, its top
    // stays at the skin's, so a collar sits under the chin instead of riding over the face.
    const girdle = add(sk.chest, apply(sk.torsoBasis, [0, p.girdle.drop - by, -0.3]))
    const cut = p.girdle.cut
    prims.push(
        ellipsoid(girdle, sk.torsoBasis, inflate(p.girdle.radii, by), m, "torso", {
            keep: q => q[1] > cut,
            exponent: p.girdle.exponent,
        }),
    )
    for (const side of SIDES) {
        // Side-on, the far deltoid would show past the throat, under the chin, where LPC leaves a
        // notch between jaw and chest: three-quarters on, it is drawn hidden behind the chest.
        if ((facing === "left" || facing === "right") && side === farSide(facing)) continue
        const s = sideSign(side)
        const cap = add(sk[side].shoulder, apply(sk.torsoBasis, [s * 0.3, 0.5, 0]))
        const r = p.deltoidRadius + by
        // Its own part: the shoulder is where the arm flows into the chest, outlined against neither.
        prims.push(ellipsoid(cap, sk.torsoBasis, [r, r, r], m, "shoulder"))
    }
    if (p.pecs) {
        // Pectorals: wide, flat slabs across the upper chest either side of the breastbone.
        for (const s of [1, -1]) {
            const at = add(chest, apply(sk.torsoBasis, [s * p.torso[0] * 0.36, p.torso[1] * 0.22, p.torso[2] * 0.72]))
            prims.push(ellipsoid(at, sk.torsoBasis, [p.pecs * 1.25 + by, p.pecs * 0.8 + by, p.pecs * 0.62 + by], m, "torso", { exponent: [2.4, 2.2, 2] }))
        }
    }
    if (p.bust) {
        // A woman's bust, on the chest's front below the collarbones: part of the chest, so a
        // shirt or a breastplate (this same upperBody, inflated) covers it. A part of its own, so
        // where it stands out from the chest -- under it -- the raster draws the seam line LPC
        // draws there: front-on, that line is what shows it.
        for (const s of [1, -1]) {
            // Far enough forward to stand proud of the chest's front: a profile shows it.
            // Set forward and low enough that its underside overhangs the ribcage turning back under
            // it: there it stands far enough proud for the seam line below it.
            const at = add(chest, apply(sk.torsoBasis, [s * p.torso[0] * 0.47, -p.torso[1] * 0.2, p.torso[2] * 1.0]))
            prims.push(ellipsoid(at, sk.torsoBasis, [p.bust + by, p.bust * 0.9 + by, p.bust * 0.85 + by], m, "bust"))
        }
    }
    return prims
}

/**
 * Hips: the pelvis, and a woman's waist above it. `legWear` stops at the natural waist -- the waist
 * volume's middle -- as trousers and skirts do, instead of riding up the ribs.
 */
const lowerBody = (sk: Skeleton, p: Proportions, m: Material, by = 0, keep?: (q: Vec3) => boolean, legWear = false): Primitive[] => {
    const prims: Primitive[] = [
        ellipsoid(p.pelvisLow ? add(sk.pelvis, apply(sk.pelvisBasis, [0, -p.pelvisLow, 0])) : sk.pelvis, sk.pelvisBasis, inflate(p.pelvis, by), m, "torso", {
            exponent: p.pelvisRoundness,
            keep,
        }),
    ]
    if (p.waist) {
        // The waist volume shares the pelvis's frame; a garment's `keep` (its top edge) is in the
        // pelvis's frame too, so test it there.
        const lift = p.waistHeight ?? 0, low = p.pelvisLow ?? 0
        const garment = keep && ((q: Vec3) => keep([q[0], q[1] + lift + low, q[2]]))
        const waistKeep = legWear ? (q: Vec3) => q[1] < 0 && (!garment || garment(q)) : garment
        prims.push(ellipsoid(add(sk.pelvis, apply(sk.pelvisBasis, [0, lift, 0])), sk.pelvisBasis, inflate(p.waist, by), m, "torso", {
            keep: waistKeep,
        }))
    }
    return prims
}

/** The middle of a closed hand: where a held tool passes through it. */
export const fistCenter = (sk: Skeleton, p: Proportions, side: Side): Vec3 => {
    const j = sk[side]
    return p.handLength
        ? add(j.hand, scale(normalize(sub(j.hand, j.elbow)), p.handLength / 2))
        : add(j.hand, apply(sk.torsoBasis, [-sideSign(side) * p.handInset, 0, 0]))
}

/**
 * Which way a blade runs from the fist. A blade sits lower in the hand than an axe's haft stands:
 * halfway between the grip and the forearm's line, so at rest it points forward-down past the
 * thigh and in a cut it runs on straight from the arm.
 */
export const bladeAxis = (j: LimbJoints): Vec3 => normalize(add(normalize(j.grip), normalize(sub(j.hand, j.elbow))))

/**
 * Which way a sword's blade runs from the fist: along the grip, as the axe's haft does -- carried,
 * up and ahead of the fist, as LPC carries its longsword; in a cut, wherever the wrist turns it.
 */
export const swordAxis = (j: LimbJoints): Vec3 => normalize(j.grip)

/** How far behind the sword hand's fist the other hand takes a two-handed grip. */
export const twoHandSpan = (p: Proportions): number => p.handRadius * 2

/** Body armour Ember draws: a leather jerkin, a legionary's banded cuirass, a plate breastplate. */
export type ArmorKind = "leather" | "legion" | "plate"

/**
 * Paint ellipsoids by where a point of them is on the body and which way the surface faces there
 * (their decals get local points; the normal is the superellipsoid's gradient, as the raster's).
 */
type Paint = (world: Vec3, normal: Vec3) => number | undefined
const painted = (prims: Primitive[], paint: Paint): Primitive[] =>
    prims.map(prim => {
        if (prim.kind !== "ellipsoid") return prim
        const e = typeof prim.exponent === "number" ? [prim.exponent, prim.exponent, prim.exponent] : prim.exponent ?? [2, 2, 2]
        return {
            ...prim,
            decal: (q: Vec3) => {
                const g = q.map((v, i) => (Math.sign(v) * Math.pow(Math.abs(v) / prim.radii[i], e[i] - 1)) / prim.radii[i]) as Vec3
                return paint(add(prim.center, apply(prim.basis, q)), normalize(apply(prim.basis, g)))
            },
        }
    })

/**
 * Metal's own light, for a body turned to `facing`. Polished metal shows what it reflects, not how
 * much light falls on it: where the reflection of the camera's view looks up at the sky it is lit,
 * brightest just above the horizon, then a hard dark band where it starts to show the ground,
 * easing a little lighter below; plus a tight glint toward the lamp. The broad lit side cloth gets
 * reads, on a cuirass, as a t-shirt.
 */
const sheen = (m: Material, facing: Facing) => {
    const turn = rotY(FACING_YAW[facing])
    const light = DEFAULT_RASTER.light
    const view: Vec3 = [0, Math.sin(DEFAULT_RASTER.pitch), Math.cos(DEFAULT_RASTER.pitch)]
    const half = normalize(add(light, view))
    return (normal: Vec3): number => {
        const n = apply(turn, normal)
        if (dot(n, half) > 0.95) return m.light
        // The view reflected off the surface: how far up (sky) or down (ground) it looks.
        const skyward = 2 * dot(n, view) * n[1] - view[1]
        if (dot(n, light) < -0.35) return m.deep
        return skyward > 0.45 ? m.base : skyward > 0.08 ? m.light : skyward > -0.22 ? m.deep : m.shade
    }
}

/** Paint over prims' own decals (theirs still shows wherever `paint` leaves a point alone). */
const overPainted = (prims: Primitive[], paint: (world: Vec3, normal: Vec3) => number | undefined): Primitive[] =>
    prims.map(prim => {
        if (prim.kind !== "ellipsoid") return prim
        const own = prim.decal
        const [over] = painted([prim], paint) as Ellipsoid[]
        return { ...over, decal: (q: Vec3) => over.decal!(q) ?? own?.(q) }
    })

/** Painted light for a body turned to `facing`: a ramp step, 0 lit to 3 deep (decals skip the raster's shading). */
const lighting = (facing: Facing) => {
    const turn = rotY(FACING_YAW[facing])
    const light = DEFAULT_RASTER.light
    return (normal: Vec3): number => {
        const lit = dot(apply(turn, normal), light)
        return lit > 0.6 ? 0 : lit > 0.05 ? 1 : lit > -0.45 ? 2 : 3
    }
}
const tone = (m: Material, step: number): number => [m.light, m.base, m.shade, m.deep][step]

/**
 * A head of hair as a few numbers, so every one of the game's styles is a record, not a drawing:
 * how far it stands off the skull, how low the fringe comes, how long it falls, and what it has
 * besides -- sides shaved, something on top, a tie, a texture.
 */
export interface HairDesign {
    /** Stand-off of the cap over the skull: 0.3 a buzz cut, 0.9 a short cut, 3 an afro. */
    volume: number
    /** The front hairline: 0 high off the brow, 0.35 a short cut's, 1 bangs to the eyebrows. */
    fringe: number
    /** Down the back: to the nape, framing the face to the jaw, or falling to the shoulders, mid-back, waist. */
    length: "nape" | "jaw" | "shoulder" | "back" | "waist"
    /** Sides: shaved to the skin (a crest left on top), or faded close. The crown: bald. */
    sides?: "shaved" | "faded"
    crown?: "bald"
    top?: "spikes" | "tufts" | "tuft" | "tall-tuft" | "crest" | "tall-crest" | "flat" | "quiff" | "beehive"
    tie?: "ponytail" | "high-ponytail" | "bun" | "topknot" | "bunches" | "braid"
    texture?: "curls" | "locks" | "rows"
}

const D = (volume: number, fringe: number, length: HairDesign["length"], more: Partial<HairDesign> = {}): HairDesign =>
    ({ volume, fringe, length, ...more })

/**
 * Ember's own styles (crop, short, long, ponytail -- drawn exactly as before they were records) and
 * each of the game's LPC styles as the nearest design (`long` and `ponytail` are both).
 */
export const HAIR_DESIGNS: Record<string, HairDesign> = {
    crop: D(0.5, 0, "nape"),
    short: D(0.9, 0.35, "nape"),
    long: D(0.9, 0.35, "shoulder"),
    ponytail: D(0.9, 0.35, "nape", { tie: "ponytail" }),
    afro: D(3, 0.3, "nape", { texture: "curls" }),
    balding: D(0.6, 0, "nape", { crown: "bald" }),
    bangs: D(1, 1, "nape"),
    bangs_bun: D(1, 1, "nape", { tie: "bun" }),
    bangslong: D(1, 1, "back"),
    bangslong2: D(1, 1, "waist"),
    bangsshort: D(0.8, 0.9, "nape"),
    bedhead: D(1.2, 0.5, "nape", { top: "tufts" }),
    bob: D(1, 0.7, "jaw"),
    bob_side_part: D(1, 0.5, "jaw"),
    braid: D(0.9, 0.35, "nape", { tie: "braid" }),
    braid2: D(0.9, 0.35, "nape", { tie: "braid" }),
    bunches: D(0.9, 0.6, "nape", { tie: "bunches" }),
    buzzcut: D(0.3, 0.1, "nape"),
    cornrows: D(0.4, 0.2, "nape", { texture: "rows" }),
    cowlick: D(0.9, 0.35, "nape", { top: "tuft" }),
    cowlick_tall: D(0.9, 0.35, "nape", { top: "tall-tuft" }),
    curly_long: D(1.4, 0.4, "back", { texture: "curls" }),
    curly_short: D(1.4, 0.4, "nape", { texture: "curls" }),
    curtains: D(1, 0.6, "jaw"),
    curtains_long: D(1, 0.6, "shoulder"),
    dreadlocks_long: D(1.2, 0.5, "back", { texture: "locks" }),
    dreadlocks_short: D(1.2, 0.5, "jaw", { texture: "locks" }),
    flat_top_fade: D(0.9, 0.2, "nape", { sides: "faded", top: "flat" }),
    flat_top_straight: D(0.9, 0.2, "nape", { top: "flat" }),
    half_up: D(1, 0.4, "shoulder", { tie: "bun" }),
    halfmessy: D(1.1, 0.5, "jaw", { top: "tufts" }),
    high_and_tight: D(0.7, 0.15, "nape", { sides: "faded" }),
    high_ponytail: D(0.9, 0.3, "nape", { tie: "high-ponytail" }),
    idol: D(0.9, 0.2, "nape", { top: "quiff" }),
    jewfro: D(1.9, 0.4, "nape", { texture: "curls" }),
    lob: D(1, 0.5, "shoulder"),
    long_band: D(1, 0.3, "back"),
    long_center_part: D(1, 0.4, "back"),
    long_messy: D(1.2, 0.5, "back", { top: "tufts" }),
    long_messy2: D(1.2, 0.6, "back", { top: "tufts" }),
    long_tied: D(0.9, 0.35, "nape", { tie: "ponytail" }),
    longhawk: D(0.4, 0.1, "nape", { sides: "shaved", top: "tall-crest" }),
    longknot: D(1, 0.35, "shoulder", { tie: "topknot" }),
    loose: D(1.1, 0.4, "back"),
    messy1: D(1.1, 0.5, "nape", { top: "tufts" }),
    messy2: D(1.1, 0.6, "nape", { top: "tufts" }),
    messy3: D(1.2, 0.5, "jaw", { top: "tufts" }),
    mop: D(1.4, 1, "jaw"),
    natural: D(2.2, 0.3, "nape", { texture: "curls" }),
    page: D(1, 1, "jaw"),
    page2: D(1, 0.9, "jaw"),
    part2: D(0.9, 0.4, "nape"),
    parted: D(0.9, 0.4, "nape"),
    pixie: D(0.8, 0.7, "nape"),
    plain: D(0.9, 0.35, "nape"),
    ponytail2: D(0.9, 0.35, "nape", { tie: "ponytail" }),
    princess: D(1, 0.4, "back", { tie: "topknot" }),
    sara: D(1.1, 0.5, "back"),
    shorthawk: D(0.4, 0.1, "nape", { sides: "shaved", top: "crest" }),
    shortknot: D(0.9, 0.35, "nape", { tie: "topknot" }),
    shoulderl: D(1, 0.6, "shoulder"),
    shoulderr: D(1, 0.6, "shoulder"),
    single: D(0.9, 0.35, "nape", { tie: "braid" }),
    spiked: D(0.8, 0.3, "nape", { top: "spikes" }),
    spiked2: D(0.8, 0.4, "nape", { top: "spikes" }),
    spiked_beehive: D(0.9, 0.3, "nape", { top: "beehive" }),
    spiked_liberty: D(0.6, 0.2, "nape", { top: "spikes", sides: "faded" }),
    spiked_liberty2: D(0.6, 0.2, "nape", { top: "spikes", sides: "faded" }),
    spiked_porcupine: D(0.8, 0.3, "nape", { top: "spikes" }),
    swoop: D(1, 0.7, "nape"),
    twists_fade: D(1.1, 0.3, "nape", { sides: "faded", texture: "locks" }),
    twists_straight: D(1.1, 0.3, "nape", { texture: "locks" }),
    unkempt: D(1.2, 0.6, "jaw", { top: "tufts" }),
    wavy: D(1.2, 0.5, "back", { texture: "curls" }),
    xlong: D(1, 0.4, "waist"),
}

/** The design for a look's hair style; none for bald (or `none`) and for a style it doesn't know, short. */
export const hairDesignFor = (style: string): HairDesign | undefined =>
    style === "bald" || style === "none" ? undefined : HAIR_DESIGNS[style] ?? HAIR_DESIGNS.short

/**
 * Hair from a design: a shell over the skull cut to its hairline, and what falls or stands off it.
 * Sizes are set for the LPC head (10.05 x 8.7 x 9.15) and scaled to this one's (`k`); features of
 * their own keep a few pixels on a small head (`s`), or a realist's spikes would be specks.
 */
const hairOf = (sk: Skeleton, p: Proportions, d: HairDesign, m: Material): Primitive[] => {
    const [a, b, c] = p.head
    const k = b / 8.7
    const s = Math.max(k, 0.6)
    const at = (v: Vec3) => add(sk.head, apply(sk.headBasis, v))
    const prims: Primitive[] = []
    const long = d.length === "shoulder" || d.length === "back" || d.length === "waist"
    // The cap: at least most of a pixel thick, so a small head's hair still shows past the skull.
    // Volume past a short cut's grows faster: an afro must double a small head, not pad it -- on a
    // head smaller than LPC's (a realist's) by more than the head's own scale, or a full style is a
    // cap. Long hair has body a short cut doesn't: it stands further off.
    const full = k < 1 ? Math.max(k * 1.3, 2) : k * 1.3
    const lift = Math.min(d.volume, 1) * Math.max(k, 0.7) + Math.max(0, d.volume - 1) * full + (long ? Math.max(k, 0.7) : 0)
    // Volume past a pixel or so grows up and back, off the face: inflated all round, a big head of
    // hair stands out in front of the face too, and the hairline cut leaves a brim over the eyes.
    const extra = Math.max(0, lift - 1.2)
    const center = at([0, 0.5 * k + 0.35 * extra, -0.4 * k - 0.45 * extra])
    const radii: Vec3 = [a + lift, b + lift - 0.25 * extra, c + lift - 0.45 * extra]
    // The hairline: above the brow at the front, lower around the back and sides.
    // (A small head's long hair comes lower, or front-on it is a band over a bare crown.)
    const brow = p.eyeHeight + b * (0.62 - 0.4 * d.fringe) - (k < 1 && long ? 0.8 : 0) - 0.35 * extra
    // A full head of hair (an afro, a mop) comes round the head, not just over it: a dome cut off
    // above the ears reads as a mushroom.
    const bushy = d.volume >= 1.4
    const nape = d.length === "nape" && !bushy ? -b * 0.5 : -b * 0.9
    // Hair to the jaw or longer comes down over the ears, framing the face.
    const side = d.length === "jaw" || long || bushy ? -b * 0.85 : brow - 3 * k
    // (Bushy hair stands round the face at the sides too: the face shows through a window in it.)
    const sides = (q: Vec3) => Math.abs(q[0]) > a * 0.8 && q[1] > side && (bushy || q[2] < c * 0.2)
    let keep = (q: Vec3) => q[1] > brow || (q[2] < -c * 0.15 && q[1] > nape) || sides(q)
    if (d.sides === "shaved") {
        const strip = keep
        keep = q => strip(q) && Math.abs(q[0]) < a * 0.38
    }
    if (d.sides === "faded") {
        // Close on the sides and back below the crown: a thin second cap there.
        const top = keep
        keep = q => top(q) && q[1] > b * 0.25
        prims.push(ellipsoid(center, sk.headBasis, inflate(p.head, 0.3 * Math.max(k, 0.7)), m, "hair", {
            exponent: p.headRoundness,
            keep: q => q[1] <= b * 0.3 && ((q[2] < -c * 0.15 && q[1] > -b * 0.5) || sides(q)),
        }))
    }
    if (d.crown === "bald") {
        const ring = keep
        keep = q => ring(q) && q[1] < b * 0.3 && q[2] < c * 0.1
    }
    prims.push(ellipsoid(center, sk.headBasis, radii, m, "hair", { exponent: p.headRoundness, keep }))
    if (long) {
        // Falling past the nape: a sheet from the back of the head down the back, as far as the
        // length goes -- to the shoulders, the shoulder blades, the waist -- measured on the body, not
        // the head (sized off a small head it hid inside the neck), standing off the back and a shirt
        // over it. Seen from the front it shows either side of the neck, behind the shoulders.
        const torso = sk.torsoBasis
        const top = dot(sub(sk.head, sk.chest), apply(torso, [0, 1, 0]))
        const end = d.length === "shoulder" ? p.shoulderHeight - 0.5 : d.length === "back" ? -p.torso[1] * 0.35 : -p.spine * 0.8
        const behind = p.torso[2] + 1.2
        prims.push(ellipsoid(add(sk.chest, apply(torso, [0, (top + end) / 2, -behind])), torso,
            [a + lift, (top - end) / 2, 1.4], m, "hair", { exponent: [2, 3.2, 2] }))
        // And a lock from each temple down past the jaw and forward over the shoulder, framing the
        // face (behind the shoulder it hid there, front-on).
        for (const x of [-1, 1]) {
            const temple = at([x * a * 0.9, -b * 0.1, -c * 0.05])
            const shoulder = add(sk.chest, apply(torso, [x * (p.neckRadius + 1.6), p.shoulderHeight + 0.6, p.torso[2] * 0.15]))
            prims.push(capsule(temple, shoulder, Math.max(1.3, 1.4 * k) + lift * 0.3, m, "hair"))
        }
    }
    if (d.length === "jaw") {
        // Framing the face: a fall over each ear to the jaw.
        for (const x of [-1, 1]) prims.push(ellipsoid(at([x * a * 0.82, -b * 0.45, -c * 0.15]), sk.headBasis, [Math.max(1.6 * s, 1.3) + lift * 0.5, b * 0.55, c * 0.6], m, "hair"))
    }
    // On top.
    const spike = (dir: Vec3, length: number, r: number) => {
        const out = normalize(apply(sk.headBasis, dir))
        const base = add(center, scale(out, b * 0.75))
        prims.push(segment(base, add(base, scale(out, length)), r, m, "hair"))
    }
    if (d.top === "spikes") for (const [x, z] of [[0, 0.5], [-0.6, 0.15], [0.6, 0.15], [-0.4, -0.45], [0.4, -0.45], [0, -0.1]]) spike([x, 1, z], 6.5 * s, 1.6 * s)
    if (d.top === "tufts") for (const [x, z] of [[-0.45, 0.3], [0.35, 0.4], [0.1, -0.35], [-0.25, -0.1]]) spike([x, 1, z], 3 * s, 1.4 * s)
    if (d.top === "tuft" || d.top === "tall-tuft") spike([0.1, 1, 0.6], (d.top === "tuft" ? 2.5 : 4.5) * s, 1.3 * s)
    if (d.top === "crest" || d.top === "tall-crest") {
        const tall = d.top === "crest" ? 2.4 : 4.5
        prims.push(ellipsoid(at([0, b + lift + tall * s * 0.4, -c * 0.1]), sk.headBasis, [1.3 * s, tall * s, c * 0.85], m, "hair"))
    }
    if (d.top === "flat") prims.push(ellipsoid(at([0, b * 0.85 + lift, -c * 0.05]), sk.headBasis, [a * 0.88, 2.2 * s, c * 0.82], m, "hair", { exponent: [2.6, 6, 2.6] }))
    if (d.top === "quiff") prims.push(ellipsoid(at([0, b * 0.85 + lift + 0.8 * s, c * 0.55]), sk.headBasis, [a * 0.75, 3.2 * s, 3.8 * s], m, "hair"))
    if (d.top === "beehive") prims.push(ellipsoid(at([0, b * 1.25 + lift, -c * 0.15]), sk.headBasis, [a * 0.8, b * 0.8, c * 0.8], m, "hair"))
    // Tied.
    if (d.tie === "ponytail") {
        const root = at([0, 2.5 * k, -c - 0.6 * k])
        prims.push(capsule(root, add(root, apply(sk.torsoBasis, [0, -10 * k, -2.2 * k])), 1.9 * k, m, "hair"))
    }
    if (d.tie === "high-ponytail") {
        const root = at([0, b * 0.75, -c * 0.85])
        prims.push(capsule(root, add(root, apply(sk.torsoBasis, [0, -12 * s, -2.6 * s])), 1.8 * s, m, "hair"))
    }
    if (d.tie === "bun") prims.push(ellipsoid(at([0, b * 0.25, -c - 1.4 * s]), sk.headBasis, [2.6 * s, 2.6 * s, 2.4 * s], m, "hair"))
    if (d.tie === "topknot") prims.push(ellipsoid(at([0, b + lift + 1.6 * s, -c * 0.25]), sk.headBasis, [2.3 * s, 2.2 * s, 2.3 * s], m, "hair"))
    if (d.tie === "bunches") {
        for (const x of [-1, 1]) {
            const root = at([x * (a + 0.4 * s), b * 0.15, -c * 0.35])
            prims.push(capsule(root, add(root, apply(sk.headBasis, [x * 2.2 * s, -8 * s, -0.8 * s])), 1.6 * s, m, "hair"))
        }
    }
    if (d.tie === "braid") {
        // Plaited: a chain of knots down the back.
        for (let i = 0; i < 6; i++) prims.push(ellipsoid(add(at([0, -b * 0.4, -c - 0.4 * s]), apply(sk.torsoBasis, [0, -i * 2.2 * s, -0.3 * i * s])), sk.headBasis, [1.4 * s, 1.3 * s, 1.3 * s], m, "hair"))
    }
    if (!d.texture) return prims
    // Texture, painted on: curls a broken speckle, locks and rows strands a pixel apart.
    const up = apply(sk.headBasis, [0, 1, 0]), across = apply(sk.headBasis, [1, 0, 0]), ahead = apply(sk.headBasis, [0, 0, 1])
    return painted(prims, w => {
        const v = sub(w, sk.head)
        if (d.texture === "curls") {
            // Scattered: a linear pattern of the cells lines up into stripes across a curved shell.
            const [i, j, l] = [Math.floor(dot(v, across) / 1.3), Math.floor(dot(v, up) / 1.3), Math.floor(dot(v, ahead) / 1.3)]
            let h = Math.imul(i, 374761393) + Math.imul(j, 668265263) + Math.imul(l, 1274126177)
            h = Math.imul(h ^ (h >>> 13), 1274126177)
            return ((h ^ (h >>> 16)) >>> 0) % 4 === 0 ? m.deep : undefined
        }
        if (d.texture === "rows") return ((dot(v, across) / 1.6) % 1 + 1) % 1 < 0.35 && dot(v, up) > 0 ? m.deep : undefined
        const around = Math.atan2(dot(v, ahead), dot(v, across)) * (a + lift)
        return ((around / 1.8) % 1 + 1) % 1 < 0.3 ? m.deep : undefined
    })
}

/** How footwear is made (EmberLook.shoes). */
export type ShoeCut = "shoes" | "boots" | "slippers" | "sandals" | "sabatons"

/** The dark of a skull's sockets and the hollow behind the ribs. */
const HOLLOW = rampFrom("#3b3330")

/**
 * A skeleton on the same rig as a living body, so every clip, weapon and garment works on it: a
 * skull with dark sockets, nose and teeth; a ribcage of bands with the hollow dark behind them, a
 * sternum and collarbones; a spine down to a pelvic bowl; limb bones with knobbed joints; bony
 * hands and feet. Bone is a few pixels of ivory at this size: every bone stays two or three pixels
 * thick (forearm and shin are one bone each -- a pair is too fine to read), and the skull is drawn
 * large, as pixel-art skeletons are, so its sockets and teeth show.
 */
const bonesOf = (sk: Skeleton, p: Proportions, facing: Facing, BONE: Material): Primitive[] => {
    const prims: Primitive[] = []
    const lit = lighting(facing)
    const [a, b, c] = p.head
    // ---- skull -------------------------------------------------------------------------------
    const cranium = ellipsoid(add(sk.head, apply(sk.headBasis, [0, p.craniumLift + 0.9, -0.2])), sk.headBasis, [a * 1.25, b * 1.18, c * 1.12], BONE, "head", {
        exponent: p.headRoundness,
    })
    const jaw = ellipsoid(add(sk.head, apply(sk.headBasis, [0, -b * 0.62, c * 0.38])), sk.headBasis, [a * 0.9, b * 0.48, c * 0.6], BONE, "head", {
        exponent: [2.4, 2.6, 2],
    })
    const across = apply(sk.headBasis, [1, 0, 0]), up = apply(sk.headBasis, [0, 1, 0]), ahead = apply(sk.headBasis, [0, 0, 1])
    // Sockets, the nose and the teeth by their direction from the skull's centre and how wide they
    // look from it: on the shell wherever it is, so a profile shows the near socket at the front of
    // the skull too. Sized so the two sockets stay apart on a small head.
    const skull = add(sk.head, apply(sk.headBasis, [0, p.craniumLift + 0.9, -0.2]))
    const toward = (x: number, y: number) => normalize(apply(sk.headBasis, [x, y - p.craniumLift - 0.9, c]))
    // Side-on the head is turned nearly to profile: sockets that far round would sit on the
    // skull's outline, so there they are set further back, the near one inside the face.
    const profile = facing === "left" || facing === "right"
    const eyeY = p.eyeHeight + b * 0.1, eyeX = Math.max(a * (profile ? 0.9 : 0.5), 1.7)
    const sockets = [toward(-eyeX, eyeY), toward(eyeX, eyeY)]
    const socketWide = Math.cos(Math.atan2(Math.max(a * (profile ? 0.42 : 0.3), profile ? 1.6 : 1.2), c))
    const nose = toward(0, eyeY - Math.max(a * 0.4, 1.5)), noseWide = Math.cos(Math.atan2(Math.max(a * 0.13, 0.6), c))
    const teeth = -b * 0.52
    prims.push(...painted([cranium, jaw], (w, n) => {
        const from = normalize(sub(w, skull))
        // Holes take no light: always the darkest.
        if (sockets.some(d => dot(from, d) > socketWide) || dot(from, nose) > noseWide) return HOLLOW.deep
        const v = sub(w, sk.head)
        const x = dot(v, across), y = dot(v, up)
        if (dot(v, ahead) > c * 0.3 && Math.abs(y - teeth) < Math.max(0.55, b * 0.09) && Math.abs(x) < a * 0.6 && ((x / 1.1) % 1 + 1) % 1 < 0.4) {
            return HOLLOW.base
        }
        return undefined
    }))
    // ---- neck and spine ----------------------------------------------------------------------
    const neckTop = add(sk.head, apply(sk.headBasis, [0, -b * 0.55, -c * 0.25]))
    const neckBase = add(sk.chest, apply(sk.torsoBasis, [0, p.torso[1] * 0.85, -p.torso[2] * 0.3]))
    for (let i = 0; i < 3; i++) prims.push(joint(lerp(neckBase, neckTop, (i + 0.5) / 3), 1.1, BONE, "torso"))
    const lumbarTop = add(sk.chest, apply(sk.torsoBasis, [0, -p.torso[1] * 0.55, -p.torso[2] * 0.35]))
    const sacrum = add(sk.pelvis, apply(sk.pelvisBasis, [0, p.pelvis[1] * 0.3, -p.pelvis[2] * 0.4]))
    for (let i = 0; i < 4; i++) prims.push(ellipsoid(lerp(lumbarTop, sacrum, (i + 0.5) / 4), sk.torsoBasis, [1.4, 0.9, 1.2], BONE, "torso"))
    // ---- ribcage -----------------------------------------------------------------------------
    const [tx, ty, tz] = p.torso
    const ribsAt = add(sk.chest, apply(sk.torsoBasis, [0, ty * 0.15, p.posture]))
    const ribRadii: Vec3 = [tx * 0.82, ty * 0.78, tz * 0.88]
    const period = Math.max(1.9, ty * 0.3)
    prims.push(ellipsoid(ribsAt, sk.torsoBasis, ribRadii, BONE, "torso", {
        exponent: p.torsoRoundness,
        // Bands of rib down to the floating ribs, open below.
        keep: q => q[1] > -ribRadii[1] * 0.7 && (((q[1] + ribRadii[1]) / period) % 1) < 0.55,
    }))
    // The hollow behind the ribs, dark between them.
    prims.push(ellipsoid(ribsAt, sk.torsoBasis, [ribRadii[0] * 0.8, ribRadii[1] * 0.8, ribRadii[2] * 0.75], HOLLOW, "torso", {
        exponent: p.torsoRoundness,
        keep: q => q[1] > -ribRadii[1] * 0.55,
    }))
    // Sternum down the front; collarbones out to the shoulders.
    const sternumTop = add(ribsAt, apply(sk.torsoBasis, [0, ribRadii[1] * 0.75, ribRadii[2] * 0.75]))
    prims.push(capsule(sternumTop, add(ribsAt, apply(sk.torsoBasis, [0, -ribRadii[1] * 0.2, ribRadii[2] * 0.95])), 0.8, BONE, "torso"))
    for (const side of SIDES) prims.push(capsule(sternumTop, sk[side].shoulder, 0.75, BONE, "torso"))
    // ---- pelvis: an open bowl ----------------------------------------------------------------
    const [px, py, pz] = p.pelvis
    prims.push(ellipsoid(add(sk.pelvis, apply(sk.pelvisBasis, [0, py * 0.15, 0])), sk.pelvisBasis, [px * 0.95, py * 0.55, pz * 0.62], BONE, "torso", {
        exponent: [2.4, 2.2, 2.2],
        keep: q => q[1] < py * 0.25,
    }))
    // ---- limbs -------------------------------------------------------------------------------
    for (const side of SIDES) {
        const j = sk[side]
        const arm = armOf(side), leg = legOf(side)
        prims.push(as(joint(j.shoulder, Math.max(1.5, p.upperArmRadius * 0.62), BONE, arm), "upperArm"))
        prims.push(as(capsule(j.shoulder, j.elbow, Math.max(1.25, p.upperArmRadius * 0.5), BONE, arm), "upperArm"))
        prims.push(as(joint(j.elbow, Math.max(1.4, p.forearmRadius * 0.68), BONE, arm), "forearm"))
        prims.push(as(capsule(j.elbow, j.hand, Math.max(1.1, p.forearmRadius * 0.52), BONE, arm), "forearm"))
        prims.push(as(ellipsoid(fistCenter(sk, p, side), sk.torsoBasis, [p.handRadius * 0.6, p.handRadius * 0.7, p.handRadius * 0.6], BONE, arm, { exponent: 2.6 }), "hand"))
        prims.push(as(joint(j.hip, Math.max(1.6, p.thighRadius * 0.55), BONE, leg), "thigh"))
        prims.push(as(capsule(j.hip, j.knee, Math.max(1.35, p.thighRadius * 0.47), BONE, leg), "thigh"))
        prims.push(as(joint(j.knee, Math.max(1.5, p.shinRadius * 0.68), BONE, leg), "shin"))
        prims.push(as(capsule(j.knee, j.ankle, Math.max(1.15, p.shinRadius * 0.5), BONE, leg), "shin"))
        prims.push(as(joint(j.ankle, Math.max(0.9, p.shinRadius * 0.4), BONE, leg), "foot"))
        prims.push(as(foot(j.heel, j.toe, p.footRadius * 0.75, (facing === "left" || facing === "right" ? p.footProfileWidth : p.footWidth) * 0.75, BONE, leg), "foot"))
    }
    return prims
}

/** How a pair of leg wear is cut (EmberLook.pants). */
export type PantsCut = "trousers" | "shorts" | "pantaloons" | "skirt" | "legion" | "greaves"

/**
 * What Ember draws, by item type: the content's designs (model.ts), kept current as content changes.
 * A shield or what a hand holds is looked up by its type; garments by the look's models.
 */
export let EMBER_HELD_NAMES: string[] = []
export let EMBER_SHIRT_NAMES: string[] = []
export let EMBER_PANTS_NAMES: string[] = []
export let EMBER_SHOES_NAMES: string[] = []
export let EMBER_ARMOR_NAMES: string[] = []
export let EMBER_SHIELD_NAMES: string[] = []
export let EMBER_BAULDRON_NAMES: string[] = []
/** Held in both hands: a shield goes on the back (generate.ts shieldSlung). */
export let TWO_HANDED: string[] = []
/** Long enough to be carried upright, the butt toward the ground (clips.ts poleCarry). */
export let POLEARMS: string[] = []
onEmberModelsChanged(() => {
    const ids = (slot: EmberSlot) => emberDesigns(slot).map(d => d.id)
    EMBER_HELD_NAMES = ids("held")
    EMBER_SHIRT_NAMES = ids("shirt")
    EMBER_PANTS_NAMES = ids("pants")
    EMBER_SHOES_NAMES = ids("shoes")
    EMBER_ARMOR_NAMES = ids("armor")
    EMBER_SHIELD_NAMES = ids("shield")
    EMBER_BAULDRON_NAMES = ids("bauldron")
    TWO_HANDED = emberDesigns("held").filter(d => d.handling?.twoHanded).map(d => d.id)
    POLEARMS = emberDesigns("held").filter(d => d.handling?.polearm).map(d => d.id)
})

/** A worn item's look: its design's model and parameters, in the item's colour. */
const worn = (slot: EmberSlot, type: string) => {
    const d = emberDesign(slot, type)
    return { ...(d?.params ?? {}), ...(d ? { model: d.model } : {}) }
}
/** The look's shirt for a worn shirt item of `type` in `color`. */
export const wornShirt = (type: string, color: string): EmberLook["shirt"] => ({ dye: color, ...worn("shirt", type) }) as EmberLook["shirt"]
/** The look's shoes for a worn shoes item of `type` in `color`. */
export const wornShoes = (type: string, color: string): EmberLook["shoes"] => ({ dye: color, ...worn("shoes", type) })
/** The look's pants for a worn pants item of `type` in `color`. */
export const wornPants = (type: string, color: string): EmberLook["pants"] => ({ dye: color, ...worn("pants", type) })
/** The look's armour for a worn armor item of `type` in `color`. */
export const wornArmor = (type: string, color: string): EmberLook["armor"] => ({ color, ...worn("armor", type) }) as EmberLook["armor"]
/** The look's bauldron for a worn bauldron item of `type` in `color`. */
export const wornBauldron = (type: string, color: string): EmberLook["bauldron"] => ({ color, ...worn("bauldron", type) })

export const buildPrimitives = (
    sk: Skeleton,
    p: Proportions,
    look: EmberLook,
    facing: Facing = "front",
    skins: Record<string, Material> = SKIN,
): Primitive[] => {
    const skin = skins[look.skin] ?? skins.light
    const prims: Primitive[] = []
    const footWidth = facing === "left" || facing === "right" ? p.footProfileWidth : p.footWidth

    // ---- body -------------------------------------------------------------------------------
    if (look.bones) {
        prims.push(...bonesOf(sk, p, facing, BONES[look.boneColor ?? "white"] ?? BONE))
    } else {
        const bare = fleshOf(sk, p, skin, facing, footWidth, look.beast)
        const flesh = look.beast ? furred(bare, skin, facing) : bare
        prims.push(...(look.wounds ? painted(flesh, woundsOf(sk, p, facing)) : flesh))
    }
    const overBody: OverBody[] = []
    prims.push(...dressed(sk, p, look, facing, skin, footWidth, overBody))
    if (overBody.length) {
        // Paint a model lays over the body's own surfaces (a bauldron's strap across the chest): on
        // every primitive of its groups, skin, shirt or armour alike, once everything is built.
        return prims.map(prim => overBody.reduce((at, o) => (o.groups.includes(at.group) ? (overPainted([at], o.paint as Paint)[0] as Primitive) : at), prim))
    }
    return prims
}

/**
 * A muscled belly's six-pack, painted down the front of the bare chest below the pectorals: the
 * line down the middle and two across, a shade darker than the skin round them.
 */
const absOf = (sk: Skeleton, p: Proportions, skin: Material, facing: Facing) => {
    const lit = lighting(facing)
    const up = apply(sk.torsoBasis, [0, 1, 0]), across = apply(sk.torsoBasis, [1, 0, 0]), ahead = apply(sk.torsoBasis, [0, 0, 1])
    const top = p.torso[1] * 0.0, bottom = -p.torso[1] * 0.8, half = p.torso[0] * 0.3
    return (w: Vec3, n: Vec3): number | undefined => {
        const v = sub(w, sk.chest)
        const x = dot(v, across), y = dot(v, up)
        if (dot(v, ahead) <= 0 || y > top || y < bottom || Math.abs(x) > half) return undefined
        const row = (top - y) / ((top - bottom) / 3)
        const line = Math.abs(x) < 0.45 || (row % 1 < 0.22 && row > 0.5)
        return line ? tone(skin, Math.min(3, lit(n) + 1)) : undefined
    }
}

/** The living body: head, chest, hips and limbs as muscle-shaped volumes in the skin. */
/**
 * Fur on a beast's body: short strands a shade darker than the light gives each spot, scattered
 * over every volume. Hashed in each volume's own frame, so the grain moves with the body instead of
 * crawling over it as it walks; paint already on a volume (a muscled chest's abs) shows first.
 */
const furred = (prims: Primitive[], fur: Material, facing: Facing): Primitive[] => {
    const lit = lighting(facing)
    return prims.map(prim => {
        if (prim.kind !== "ellipsoid") return prim
        const e = typeof prim.exponent === "number" ? [prim.exponent, prim.exponent, prim.exponent] : prim.exponent ?? [2, 2, 2]
        const before = prim.decal
        return {
            ...prim,
            decal: (q: Vec3) => {
                const painted = before?.(q)
                if (painted !== undefined) return painted
                // Strands: cells taller than wide, about one in five drawn.
                let h = Math.imul(Math.floor(q[0] / 1.3), 374761393) + Math.imul(Math.floor(q[1] / 2.4), 668265263) + Math.imul(Math.floor(q[2] / 1.3), 1274126177)
                h = Math.imul(h ^ (h >>> 13), 1274126177)
                if (((h ^ (h >>> 16)) >>> 0) % 5 !== 0) return undefined
                const g = q.map((v, i) => (Math.sign(v) * Math.pow(Math.abs(v) / prim.radii[i], e[i] - 1)) / prim.radii[i]) as Vec3
                return tone(fur, Math.min(3, lit(normalize(apply(prim.basis, g))) + 1))
            },
        }
    })
}

const fleshOf = (sk: Skeleton, p: Proportions, skin: Material, facing: Facing, footWidth: number, beast?: EmberLook["beast"]): Primitive[] => {
    const prims: Primitive[] = []
    prims.push(...(beast === "wolf" ? [...wolfHeadOf(sk, p, skin, facing), ...wolfFurOf(sk, p, skin)] : head(sk, p, skin)))
    prims.push(capsule(sk.chest, sk.neck, p.neckRadius, skin, "torso"))
    const chest = upperBody(sk, p, skin, 0, facing)
    // A muscled chest's abs, on the chest volume itself (the first), under any shirt.
    prims.push(...(p.pecs ? [...painted(chest.slice(0, 1), absOf(sk, p, skin, facing)), ...chest.slice(1)] : chest))
    prims.push(...lowerBody(sk, p, skin))
    for (const side of SIDES) {
        const j: LimbJoints = sk[side]
        // Each joint ball belongs to the segment below it, so a joint gets one line, not two.
        prims.push(as(segment(j.shoulder, j.elbow, p.upperArmRadius, skin, armOf(side)), "upperArm"))
        prims.push(as(joint(j.elbow, p.forearmRadius * 0.85, skin, armOf(side)), "forearm"))
        prims.push(as(segment(j.elbow, j.hand, p.forearmRadius, skin, armOf(side)), "forearm"))
        if (p.handLength) {
            // A closed fist on the end of the forearm: a squarish knob, wider than the wrist.
            const tip = add(j.hand, scale(normalize(sub(j.hand, j.elbow)), p.handLength))
            prims.push(as(segment(j.hand, tip, p.handRadius, skin, armOf(side), 2.8), "hand"))
        } else {
            const fist = add(j.hand, apply(sk.torsoBasis, [-sideSign(side) * p.handInset, 0, 0]))
            prims.push(as(ellipsoid(fist, sk.torsoBasis, [p.handRadius, p.handRadius, p.handRadius], skin, armOf(side)), "hand"))
        }
        prims.push(...thighOf(sk, side, p, 0, skin, legOf(side)).map(prim => as(prim, "thigh")))
        prims.push(as(joint(j.knee, p.shinRadius * 0.9, skin, legOf(side)), "shin"))
        prims.push(...shinOf(sk, side, p, 0, skin, legOf(side)).map(prim => as(prim, "shin")))
        prims.push(as(joint(j.ankle, p.shinRadius * 0.7, skin, legOf(side)), "foot"))
        prims.push(as(foot(j.heel, j.toe, p.footRadius, footWidth, skin, legOf(side)), "foot"))
    }
    return prims
}

const WOUND = rampFrom("#8a3a34")
const GORE = rampFrom("#5a1f22")

/**
 * Where a dead body is torn open: a few wounds at fixed places on it -- the chest, a forearm, the
 * belly, a thigh, a shin, the back -- each a dark core in a raw rim, lit like the flesh round them.
 * Placed on the skeleton, so they stay put as the body moves and show from whichever side faces them.
 */
const woundsOf = (sk: Skeleton, p: Proportions, facing: Facing) => {
    const lit = lighting(facing)
    const T = (v: Vec3) => add(sk.chest, apply(sk.torsoBasis, v))
    const P = (v: Vec3) => add(sk.pelvis, apply(sk.pelvisBasis, v))
    const r = Math.max(1.2, p.torso[0] * 0.2)
    const wounds: [Vec3, number][] = [
        [T([p.torso[0] * 0.45, p.torso[1] * 0.3, p.torso[2]]), r],
        [T([-p.torso[0] * 0.3, -p.torso[1] * 0.4, p.torso[2]]), r * 0.8],
        [T([p.torso[0] * 0.2, p.torso[1] * 0.1, -p.torso[2]]), r],
        [lerp(sk.R.elbow, sk.R.hand, 0.5), r * 0.75],
        [P([p.hipWidth, -p.thigh * 0.35, p.thighRadius * 0.8]), r * 0.8],
        [lerp(sk.L.knee, sk.L.ankle, 0.4), r * 0.7],
    ]
    return (w: Vec3, n: Vec3): number | undefined => {
        for (const [at, size] of wounds) {
            const d = distance(w, at)
            if (d < size * 0.55) return tone(GORE, lit(n))
            if (d < size) return tone(WOUND, lit(n))
        }
        return undefined
    }
}

/** What the body has on: clothes, armour, hair, what it holds. */
const dressed = (sk: Skeleton, p: Proportions, look: EmberLook, facing: Facing, skin: Material, footWidth: number, overBody: OverBody[] = []): Primitive[] => {
    const prims: Primitive[] = []

    // ---- garments: inflated copies of what they cover -----------------------------------------
    // How far cloth stands off the skin, scaled for bodies slimmer than LPC's.
    const t = p.clothThickness ?? 1
    // Each slot is drawn by its model (model.ts, content's sprites/ember.json), in this order: leg
    // wear, footwear, shirt, belt, armour, bauldron, shield, then hair (the body's own) and what the
    // hand holds. A look names its garments' models and their parameters; a shield and what the
    // hand holds are item types, drawn by their designs.
    const context = { sk, p, look, facing, skin, footWidth, t }
    const wear = (slot: EmberSlot, model: string, params: object, bind?: Record<string, string>) =>
        prims.push(...(runEmberModel(slot, model, params as Record<string, unknown>, context, overBody, bind) as Primitive[]))
    if (look.pants) wear("pants", look.pants.model ?? look.pants.cut ?? "trousers", look.pants)
    if (look.shoes) wear("shoes", look.shoes.model ?? look.shoes.cut ?? "shoes", look.shoes)
    if (look.shirt) wear("shirt", look.shirt.model ?? "shirt", look.shirt)
    if (look.belt) wear("belt", look.belt.model ?? "belt", look.belt)
    if (look.armor) wear("armor", look.armor.model ?? look.armor.kind, look.armor)
    if (look.bauldron) wear("bauldron", look.bauldron.model ?? "bauldron", look.bauldron)
    const shield = look.shield && emberDesign("shield", look.shield.type)
    if (shield) wear("shield", shield.model, { ...shield.params, ...look.shield }, shield.bind)

    // ---- hair: a shell over the skull, cut away where the face shows ---------------------------
    // A skull has none.
    const design = look.hair && !look.bones && !look.beast && hairDesignFor(look.hair.style)
    if (look.hair && design) prims.push(...hairOf(sk, p, design, hairDye(look.hair.color)))

    // ---- held: what the hand holds -----------------------------------------------------------
    const held = look.held ? emberDesign("held", look.held) : undefined
    if (held) wear("held", held.model, { ...held.params, held: look.held, color: look.heldColor }, held.bind)

    return prims
}

/**
 * Only what a look wears and holds -- no body -- in the posed skeleton: an item on its own, for its
 * icon. `skin` stands in for the body's skin wherever a garment paints it (a neckline's opening).
 */
export const equipmentPrimitives = (sk: Skeleton, p: Proportions, look: EmberLook, facing: Facing, skin: Material): Primitive[] =>
    dressed(sk, p, look, facing, skin, facing === "left" || facing === "right" ? p.footProfileWidth : p.footWidth)

/**
 * Primitives scaled by `k` about the origin, never thinner than `floor` pixels where they were
 * thicker: their cuts and paint still see the unscaled shape, so they cut and paint the same.
 */
export const scalePrimitives = (prims: Primitive[], k: number, floor = 0.7): Primitive[] =>
    prims.map(prim => {
        const r = (v: number) => Math.max(v * k, Math.min(v, floor))
        if (prim.kind === "capsule") return { ...prim, a: scale(prim.a, k), b: scale(prim.b, k), r: r(prim.r) }
        const radii: Vec3 = [r(prim.radii[0]), r(prim.radii[1]), r(prim.radii[2])]
        const back = (q: Vec3): Vec3 => [q[0] * (prim.radii[0] / radii[0]), q[1] * (prim.radii[1] / radii[1]), q[2] * (prim.radii[2] / radii[2])]
        return {
            ...prim,
            center: scale(prim.center, k),
            radii,
            ...(prim.keep ? { keep: (q: Vec3) => prim.keep!(back(q)) } : {}),
            ...(prim.decal ? { decal: (q: Vec3) => prim.decal!(back(q)) } : {}),
        }
    })

/** The radius a primitive can reach from its centre, for screen-space culling. */
export const boundingSphere = (prim: Primitive): { center: Vec3; radius: number } => {
    if (prim.kind === "capsule") {
        const center = scale(add(prim.a, prim.b), 0.5)
        const half = Math.hypot(prim.a[0] - prim.b[0], prim.a[1] - prim.b[1], prim.a[2] - prim.b[2]) / 2
        return { center, radius: half + prim.r }
    }
    return { center: prim.center, radius: Math.max(prim.radii[0], prim.radii[1], prim.radii[2]) }
}

/**
 * What a model (model.ts) can use: the body's own volumes and the shapes, light and materials the
 * game's drawings are made of, and plain maths. Nothing else is reachable from a model.
 */
setModelBuiltins({
    // maths
    PI: Math.PI, min: Math.min, max: Math.max, abs: Math.abs, sqrt: Math.sqrt, pow: Math.pow, floor: Math.floor, ceil: Math.ceil,
    round: Math.round, sin: Math.sin, cos: Math.cos, tan: Math.tan, atan2: Math.atan2, hypot: Math.hypot, sign: Math.sign,
    // vectors and frames (a Mat3 is nine numbers, its columns the frame's axes)
    add, sub, scale, dot, cross, normalize, lerp, distance, length: vecLength, apply, transpose, mul, rotX, rotY, rotZ, deg, IDENTITY,
    // lists
    range: (n: number) => Array.from({ length: n }, (_, i) => i),
    values: (o: Record<string, unknown>) => Object.values(o),
    keys: (o: Record<string, unknown>) => Object.keys(o),
    // the rig
    SIDES, sideSign, farSide, legLength, fistCenter, bladeAxis, swordAxis, twoHandSpan,
    // shapes
    capsule, ellipsoid, segment, foot, joint, inflate, as, armOf, legOf,
    // the body's own volumes, `by` off the skin: what a garment is an inflated copy of
    upperBody, lowerBody, thighOf, shinOf, legFrame,
    // light and paint
    painted, overPainted, lighting, tone, sheen,
    // materials (`cloth(name)` is a dye's ramp: garments name their colour `dye`)
    cloth: dye, dye, hairDye, rampFrom, metalFrom, DYES,
})
